<template>
    <app-layout title="Check Order">

        <page-header>
            <template #title>Check Order</template>

            <template #actions>
                <!-- Print button / dropdown -->
                <!-- Retail -->
                <jet-dropdown v-if="!order.is_wholesale" align="right" width="24">
                    <template #trigger>
                        <jet-tertiary-button size="small">
                            <img src="/images/icons/print.svg" width="16" height="16" alt="Print">
                            <img class="ml-2" src="/images/icons/chevron-down.svg" width="16" height="16" alt="Print">
                        </jet-tertiary-button>
                    </template>
                    <template #content>
                        <div class="px-4 py-2 hover:bg-gray cursor-pointer text-sm" @click="handleRetailPrint('invoices')">Order</div>
                        <div class="px-4 py-2 hover:bg-gray cursor-pointer text-sm" @click="handleRetailPrint('labels')">Label</div>
                    </template>
                </jet-dropdown>

                <!-- Wholesale -->
                <jet-dropdown v-else-if="order.status == 2" align="right" width="48">
                    <template #trigger>
                        <jet-tertiary-button size="small">
                            <img src="/images/icons/print.svg" width="16" height="16" alt="Print">
                            <img class="ml-2" src="/images/icons/chevron-down.svg" width="16" height="16" alt="Print">
                        </jet-tertiary-button>
                    </template>
                    <template #content>
                        <div class="px-4 py-2 hover:bg-gray cursor-pointer text-sm" @click="handlePrint('confirmation')">Order Confirmation</div>
                        <div class="px-4 py-2 hover:bg-gray cursor-pointer text-sm" @click="handlePrint('invoice')">Order Invoice</div>
                    </template>
                </jet-dropdown>

                <jet-tertiary-button v-else type="button" class="py-1.5 sm:py-1 leading-none" @click="handlePrint('confirmation')">
                    <img src="/images/icons/print.svg" width="16" height="16" alt="Print">
                </jet-tertiary-button>

                <jet-tertiary-button type="button" class="leading-none" @click="backUrl">
                    Close
                </jet-tertiary-button>
            </template>
        </page-header>

        <div v-if="mode == 'summary'">
            <div class="flex flex-col px-3 md:px-4 lg:px-8 gap-y-4">
                <order-details :order="order" :key="order.status"/>
            </div>

            <div class="flex gap-4 py-2 px-3 md:px-4 lg:px-8">
                <div>
                    <jet-label for="checker_id" value="Current Checker:" class="w-32 align-top leading-7 whitespace-nowrap" />
                    <div class="flex flex-1 flex-col">
                        <div v-if="checker == null">No Checker Selected</div>
                        <div v-else>{{ checker.name }}</div>

                        <div>
                            <jet-secondary-button type="button" class="leading-none py-1 px-2 mr-2 align-middle" @click="showCheckerCode()">
                                Update Checker
                            </jet-secondary-button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="py-2 px-3 md:px-4 lg:px-8">
                <div v-if="!allItemsProcessed" class="my-4" >
                    <jet-button class="leading-none whitespace-nowrap" @click="startChecking()" >
                        {{ checkingStarted ? 'Continue' : 'Start' }} Checking
                    </jet-button>
                </div>

                <div v-else-if="!order.picking_checked_at" class="my-4" >
                    <jet-button class="leading-none" :disabled="!allItemsProcessedWithAuth" @click="markOrderChecked">
                        Complete Checking
                    </jet-button>
                </div>
            </div>

            <div class="flex gap-x-4 justify-between items-end py-2 px-3 md:px-4 lg:px-8">
                <!-- Substitutions -->
                <div v-if="order.allow_substitutions" class="pb-1">
                    <span class="font-bold">Substitutions Allowed</span>
                    <div class="text-xs">If an item is unavailable when preparing your order, we may substitute some or all of it for a similar item.</div>
                </div>
                <div v-else class="pb-1">
                    <span class="font-bold">Substitutions Not Allowed</span>
                    <div class="text-xs">If an item is unavailable when preparing your order, we will only give what's available without substituting for a similar item.</div>
                </div>
            </div>

            <div class="rounded-t overflow-hidden px-3 md:px-4 lg:px-8">
                <table class="table w-full mb-6">
                    <thead>
                        <tr class="align-bottom bg-gray-light border">
                            <th class="px-2 py-1 text-center"></th>
                            <th class="px-2 py-1 text-left">Product</th>
                            <th class="px-2 py-1 text-left">Picker</th>
                            <th class="px-2 py-1 text-left">Checker</th>
                            <th class="px-2 py-1 text-right">Ordered</th>
                            <th class="px-2 py-1 text-right">Picked</th>
                            <th class="px-2 py-1 text-right">Checked</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="item in order_items"
                            :key="item.id"
                            :class="{
                                'item_picked': item.checking_status == 'picked',
                                'item_shorted': item.checking_status == 'shorted' || (item.picking_status === 'shorted' && item.checking_status !== 'picked'),
                            }"
                            class="striped"
                        >
                            <td class="px-2 py-1 text-center" width="56px;">
                                <img src="/images/icons/green_check.svg" 
                                v-if="item.checking_status === 'picked' || (item.checking_status == 'shorted' && item.auth_code_id !== null)" 
                                    width="24" height="24" class="mx-auto" />
                                
                                <jet-secondary-button 
                                    v-if="(item.picking_status === 'shorted' || item.checking_status === 'shorted') && item.auth_code_id === null" 
                                    type="button"
                                    class="leading-none py-1 px-2 mr-2 align-middle" @click="showAuthCode(item)">
                                    Authorize Short
                                </jet-secondary-button>
                            </td>
                            <td v-if="order.is_wholesale" class="px-2 py-1">
                                <div v-if="item.parent_id" class="text-xs italic">
                                    Part of "{{ item.parent_item.title }}
                                    <span v-if="item.parent_item.description"> - {{ item.parent_item.description }}</span>
                                    <span v-if="item.parent_item.size"> - {{ item.parent_item.size }}</span>"
                                </div>
                                <div>
                                    {{ item.title }}
                                    <span v-if="item.description"> - {{ item.description }}</span>
                                    <span v-if="item.size"> - {{ item.size }}</span>
                                </div>
                            </td>
                            <td v-else class="px-2 py-1">
                                <div v-if="item.parent_id" class="text-xs italic">
                                    Part of "{{ item.parent_item.title }}
                                    <span v-if="item.parent_item.description"> - {{ item.parent_item.description }}</span>
                                    <span v-if="item.parent_item.description_alt"> - {{ item.parent_item.description_alt }}</span>"
                                </div>
                                <div>
                                    {{ item.title }}
                                    <span v-if="item.description"> - {{ item.description }}</span>
                                    <span v-if="item.description_alt"> - {{ item.description_alt }}</span>
                                </div>
                            </td>
                            <td class="px-2 py-1 text-left">{{ item.picker?.name }}</td>
                            <td class="px-2 py-1 text-left">{{ item.checker?.name }}</td>
                            <td class="px-2 py-1 text-right">{{ item.orig_quantity }}</td>
                            <td class="px-2 py-1 text-right">{{ item.picked_quantity }}</td>
                            <td class="px-2 py-1 text-right">{{ (item.checking_status === 'picked' || (item.checking_status === 'shorted' && item.auth_code_id !== null)) ? item.quantity : null }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- If shorted ask for the auth codes -->
            <jet-dialog-modal :show="showAuthCodeModal" @close="cancelAuthCode">
                <template #title>
                        <h3 class="text-2xl">Authorize Short</h3>
                </template>

                <template #content>
                    <form @submit.prevent="shortOrderItem">
                        <div class="mb-2">
                            <jet-label value="Code:" class="mb-0" />
                            <jet-input v-model="authCode" type="text" class="w-full" required />
                            <jet-input-error :message="authCodeError" class="m1" />
                        </div>

                        <div>
                            <jet-label value="Quantity:" class="mb-0" />
                            <jet-input v-model.number="quantity" type="number" class="w-full" required step="0.5" min="0" :max="authCodeItem.orig_quantity" />
                        </div>

                        <input type="submit" class="hidden" ref="submitAuthCode" />
                    </form>
                </template>

                <template #footer>
                    <jet-button class="mr-2" @click="$refs.submitAuthCode.click()">
                        Approve
                    </jet-button>

                    <jet-tertiary-button @click="cancelAuthCode">
                        Cancel
                    </jet-tertiary-button>
                </template>
            </jet-dialog-modal>
        </div>

        <div v-else-if="mode == 'checking' && currentItem">
            <div class="flex flex-col px-3 md:px-4 lg:px-8 gap-y-4">
                <div>
                    <jet-tertiary-button class="leading-none mx-2" @click="$inertia.visit($page.url)">
                        Pause Checking
                    </jet-tertiary-button>
                </div>

                <div>
                    <!-- Wholesale Title -->
                    <template v-if="order.is_wholesale">
                        <div v-if="currentItem.parent_id" class="text-lg italic">
                            Part of "{{ currentItem.parent_item.title }}
                            <span v-if="currentItem.parent_item.description"> - {{ currentItem.parent_item.description }}</span>
                            <span v-if="currentItem.parent_item.size"> - {{ currentItem.parent_item.size }}</span>"
                        </div>
                        <div class="text-4xl font-bold">
                            {{ currentItem.title }}
                            <span v-if="currentItem.description"> - {{ currentItem.description }}</span>
                            <span v-if="currentItem.size"> - {{ currentItem.size }}</span>
                        </div>

                        <div v-if="currentItem.product_notes" class="text-2xl font-medium">
                            {{ currentItem.product_notes }}
                        </div>
                    </template>
                    <!-- Retail Title -->
                    <template v-else>
                        <div v-if="currentItem.parent_id" class="text-lg italic">
                            Part of "{{ currentItem.parent_item.title }}
                            <span v-if="currentItem.parent_item.description"> - {{ currentItem.parent_item.description }}</span>
                            <span v-if="currentItem.parent_item.description_alt"> - {{ currentItem.parent_item.description_alt }}</span>"
                        </div>
                        
                        <div class="text-4xl font-bold">
                            {{ currentItem.title }}
                            <span v-if="currentItem.description"> - {{ currentItem.description }}</span>
                        </div>

                        <div v-if="currentItem.description_alt" class="text-2xl font-medium">
                            {{ currentItem.description_alt }}
                        </div>
                    </template>
                    
                    <div class="text-8xl font-bold mt-2">
                        <span v-if="checkOneByOne">
                            {{ currentItemCurrentQty > (currentItem.picked_quantity ?? currentItem.orig_quantity) ? (currentItem.picked_quantity ?? currentItem.orig_quantity) : currentItemCurrentQty }} of {{ (currentItem.picked_quantity ?? currentItem.orig_quantity) }}
                        </span>
                        <span v-else>
                            Picked: {{ (currentItem.picked_quantity ?? currentItem.orig_quantity) }}
                        </span>
                    </div>

                    <div v-if="currentItem.picking_status === 'shorted'" class="text-4xl font-bold">
                        Ordered: {{ currentItem.orig_quantity }}
                    </div>

                    <jet-tertiary-button class="leading-none mt-2" 
                        @click="showOverridePickedQuantityModal = true">
                        Override Picked Quantity
                    </jet-tertiary-button>
                </div>

                <div>
                    <img
                        v-show="currentItem.product.image"
                        :src="'/storage/' + currentItem.product.image"
                        :key="currentItem.product.image"
                        alt="Product Preview"
                        class="w-auto self-center rounded-lg"
                        style="height: 50vh; max-height: 50vh;"
                    />
                </div>

                <div class="whitespace-nowrap">
                    <jet-button v-if="!checkOneByOne || currentItemCurrentQty >= (currentItem.picked_quantity ?? currentItem.orig_quantity)" class="leading-none mx-6 text-2xl px-6 py-3" @click="updateOrderItem('picked')" :disabled="updatingOrderItem">
                        Checked
                    </jet-button>
                    <jet-button v-else class="leading-none mx-6 text-2xl px-6 py-3" @click="updatePartialOrderItem()">
                        Checked
                    </jet-button>
                    <jet-tertiary-button class="leading-none mx-6 text-2xl px-6 py-3" @click="updateOrderItem('shorted')" :disabled="updatingOrderItem">
                        Short
                    </jet-tertiary-button>
                    <jet-tertiary-button  class="leading-none mx-6 text-2xl px-6 py-3 ml-24" @click="skipOrderItem()" :disabled="countItemsUnprocessed <= 1 || updatingOrderItem">
                        Skip
                    </jet-tertiary-button>
                </div>
                <div v-if="updatingOrderItem" class="flex gap-x-1 text-sm mx-6">
                    <img src="/images/icons/loading-pulse.svg" height="24" width="24" class="" />
                    <div class="pt-1">
                        Saving...
                    </div>
                </div>
            </div>
        </div>

        <!-- Checker Login -->
        <jet-dialog-modal :show="showCheckerCodeModal" @close="cancelCheckerCode">
            <template #title>
                    <h3 class="text-2xl">Update Checker</h3>
            </template>

            <template #content>
                <form @submit.prevent="updateChecker">
                    <div class="mb-2">
                        <jet-label value="Code:" class="mb-0" />
                        <jet-input v-model="checkerCode" ref="checkerCode" type="text" class="w-full" required />
                        <jet-input-error :message="checkerCodeError" class="m1" />
                    </div>

                    <input type="submit" class="hidden" ref="submitCheckerCode" />
                </form>
            </template>

            <template #footer>
                <jet-button class="mr-2" @click="$refs.submitCheckerCode.click()">
                    Update
                </jet-button>

                <jet-tertiary-button @click="cancelCheckerCode">
                    Cancel
                </jet-tertiary-button>
            </template>
        </jet-dialog-modal>

        <!-- Override picked quantity -->
        <jet-dialog-modal :show="showOverridePickedQuantityModal" @close="showOverridePickedQuantityModal = false">
            <template #title>
                <h3 class="text-2xl">Overrride Picked Quantity</h3>
            </template>

            <template #content>
                <form @submit.prevent="submitOverridePickedQuantity">
                    <jet-label value="Quantity:" class="mb-0" />
                    <jet-input v-model="overridePickedQuantity" type="number" class="w-full" step="0.5" min="0" :max="currentItem.orig_quantity" required />

                    <input type="submit" class="hidden" ref="submitOverridePickedQuantity" />
                </form>
            </template>

            <template #footer>
                <jet-button class="mr-2" @click="$refs.submitOverridePickedQuantity.click()">
                    Update
                </jet-button>

                <jet-tertiary-button @click="showOverridePickedQuantityModal = false">
                    Cancel
                </jet-tertiary-button>
            </template>
        </jet-dialog-modal>

    </app-layout>
</template>

<script>
import { usePage } from '@inertiajs/inertia-vue3';
import UseUserAuth from '@/Composables/UseUserAuth';
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import JetButton from '@/Jetstream/Button';
import JetInput from '@/Jetstream/Input';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetDialogModal from '@/Jetstream/DialogModal';
import JetDropdown from '@/Jetstream/Dropdown';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import JetSecondaryButton from '@/Jetstream/SecondaryButton';
import OrderDetails from '@/Shared/Orders/OrderDetails';
import JetLabel from '@/Jetstream/Label';
import JetInputError from '@/Jetstream/InputError';
import Multiselect from '@vueform/multiselect';
import { EventBus } from '@/eventBus.js';

import '@vueform/multiselect/themes/default.css';

import axios from 'axios';

export default {
    props: ['order', 'order_items', 'origChecker'],

    setup(props) {
        const { isAdmin } = UseUserAuth(usePage().props.value.user);

        return { isAdmin }
    },

    components: {
        AppLayout,
        PageHeader,
        JetButton,
        JetInput,
        JetConfirmationModal,
        JetSecondaryButton,
        JetDangerButton,
        JetDialogModal,
        JetDropdown,
        JetTertiaryButton,
        OrderDetails,
        JetLabel,
        JetInputError,
        Multiselect,
    },

    data() {
        return {
            mode: 'summary',
            showAuthCodeModal: false,
            authCode: null,
            quantity: null,
            authCodeItem: null,
            authCodeError: null,
            authCodeErrorTimeout: null,

            checker: this.origChecker,
            showCheckerCodeModal: false,
            checkerCode: null,
            checkerCodeError: null,
            checkerCodeErrorTimeout: null,

            // checkOneByOne: !this.order.is_wholesale || (this.order.is_wholesale && (this.order.customer.pricing_level_id == 4 || this.order.customer.pricing_level_id == 5)),
            checkOneByOne: !this.order.is_wholesale,
            currentItemCurrentQty: 1,

            updatingOrderItem: false,

            //Override picked quantity
            showOverridePickedQuantityModal: false,
            overridePickedQuantity: null,
        };
    },

    methods: {
        backUrl () {
            this.$inertia.visit(route('loads.show', { load: this.order.load_order.load_id }));
        },

        updateCheckerIfNeeded (response = null, callback = null) {
            if (response === null || response === 'invalid_checker_session')
            {
                this.checker = null;
                this.showCheckerCode();
            }
            else if (typeof callback == 'function') callback();
        },

        startChecking() {
            axios.get(`/getChecker`)
            .then((response) => {
                this.updateCheckerIfNeeded(response.data, () => {
                    this.mode = 'checking';
                });
            });
        },

        markOrderChecked(){
            axios.post(route('orders.mark.checked', { order: this.order.id }))
            .then((response) => {
                if (response.data == 'success')
                {
                    this.backUrl();
                }
            });
        },

        updatePartialOrderItem() {
            this.currentItemCurrentQty++;
        },

        updateOrderItem(status) {
            this.updatingOrderItem = true;

            axios.post(`/order_item/${this.currentItem.id}/check`, { status })
            .then((response) => {
                this.updateCheckerIfNeeded(response.data, () => {
                    Object.assign(this.currentItem, response.data);
                })
            })
            .catch((error) => {
                this.errorMessage = error.response?.data?.message || error.message;
            })
            .finally(() => {
                this.updatingOrderItem = false;
            });
        },

        shortOrderItem() {
            this.authCodeError = null;
            if (this.authCodeErrorTimeout) clearTimeout(this.authCodeErrorTimeout);

            axios.post(route('orders.items.short.check', { order_item: this.authCodeItem.id, auth_code: this.authCode, quantity: this.quantity }))
            .then((response) => {
                if (response.data == 'invalid_auth_code')
                {
                    this.authCodeError = 'Invalid Authorization Code';
                    this.authCodeErrorTimeout = setTimeout(() => {
                        this.authCodeError = null;
                    }, 4 * 1000);
                }
                else
                {
                    let item = this.order_items.find(item => item.id === response.data.id);
                    if (item) {
                        Object.assign(item, response.data);
                    }
                    this.cancelAuthCode();
                }
            });
        },

        submitOverridePickedQuantity(){
            this.authCodeError = null;
            if (this.authCodeErrorTimeout) clearTimeout(this.authCodeErrorTimeout);

            axios.post(route('orders.items.override_picked_quantity', {
                override_picked_quantity: this.overridePickedQuantity,
                order_item: this.currentItem.id
            }))
            .then((response) => {
                if (response.data == 'invalid_auth_code')
                {
                    this.authCodeError = 'Invalid Authorization Code';
                    this.authCodeErrorTimeout = setTimeout(() => {
                        this.authCodeError = null;
                    }, 4 * 1000);
                }
                else
                {
                    let item = this.order_items.find(item => item.id === response.data.id);
                    if (item) {
                        Object.assign(item, response.data);
                    }
                    this.showOverridePickedQuantityModal = false;
                    this.overridePickedQuantity = null;
                }
            });
        },

        skipOrderItem() {
            this.currentItem.skipped = true;
        },

        resetSkippedItems () {
            this.order_items.forEach(item => {
                item.skipped = false;
            });
        },

        showAuthCode(item) {
            this.authCodeItem = item;
            this.showAuthCodeModal = true;
        },

        cancelAuthCode() {
            this.showAuthCodeModal = false;
            this.authCodeItem = null;
            this.authCode = null;
            this.quantity = null;
        },

        updateChecker() {
            this.checkerCodeError = null;
            if (this.checkerCodeErrorTimeout) clearTimeout(this.checkerCodeErrorTimeout);

            axios.post(route('orders.update_checker', { checker_code: this.checkerCode }))
            .then((response) => {
                if (response.data == 'invalid_checker_code')
                {
                    this.checkerCodeError = 'Invalid Checker Code';
                    this.checkerCodeErrorTimeout = setTimeout(() => {
                        this.checkerCodeError = null;
                    }, 4 * 1000);
                }
                else
                {
                    this.checker = response.data;
                    this.cancelCheckerCode();
                }
            });
        },

        showCheckerCode() {
            this.showCheckerCodeModal = true;
        },

        cancelCheckerCode() {
            this.showCheckerCodeModal = false;
            this.checkerCode = null;
        },

        // Printing
        handlePrint(type) {
            window.open((route('orders.print', { orders: this.order.id, printType: type} )), "_blank");
            if (type == 'confirmation') {
                setTimeout(()=> {
                    this.$inertia.reload();
                }, 250);
            }
            else if (type === 'invoice') {
                this.showMarkShippedAndInvoicedModal = false;

                setTimeout(()=> {
                    this.$inertia.reload();
                }, 250);
            }
        },

        handleRetailPrint(type) {
            let print_route = 'routific.print.' + type;
            if (this.shouldAutoprint(type)) axios.get(route(print_route, { orders: [this.order.id]} ));
            else window.open((route(print_route, { orders: [this.order.id]} )), "_blank");
            if(type === 'invoices') {
                setTimeout(()=> {
                    this.$inertia.reload();
                }, 250);
            }
        },
    },

    computed: {
        currentItem() {
            return this.order_items.find(item => item.checking_status === null && !item.skipped);
        },

        checkingStarted () {
            return this.order_items.some(item => item.checking_status !== null);
        },

        countItemsUnprocessed() {
            return this.order_items.filter(item => item.checking_status === null).length;
        },

        allItemsProcessed() {
            return this.order_items.every(item => item.checking_status !== null);
        },

        allItemsProcessedWithAuth() {
            return this.order_items.every((item) => {
                return item.checking_status == 'picked' || (item.checking_status == 'shorted' && item.auth_code_id);
            });
        },
    },

    watch: {
        mode () {
            this.currentItemCurrentQty = 1;
            this.resetSkippedItems();
        },

        currentItem () {
            this.currentItemCurrentQty = 1;

            if (!this.currentItem)
            {
                this.resetSkippedItems();

                this.$nextTick(() => {
                    if (!this.currentItem)
                    {
                        this.mode = 'summary';
                    }
                });
            }
        },

        showCheckerCodeModal () {
            setTimeout(() => {
                if (this.$refs.checkerCode) this.$refs.checkerCode.focus();
            }, 100)
        }
    }
};
</script>

<style scoped>
    .item_picked {
        @apply bg-green bg-opacity-20 !important;
    }
    .item_shorted {
        @apply bg-yellow-400 bg-opacity-20 !important;
    }
</style>